/**
 * t413u: HR records stages 3 and 4 (docs/feature-backlog.md §185). Payroll export, starter and leaver reports, "missing
 * details" reminders, the HR approvals widget, UK retention with a scheduled purge, subject-access export, erasure and
 * key rotation. Shared by the API and the three apps so the rules, labels and wording match everywhere.
 */
import { DateTime } from 'luxon';
import { hrDate, hrToday } from './hrDocuments';

const ZONE = 'Europe/London';

// ------------------------------------------------------------------------------------------------ permissions
/** The stage 3 and 4 permissions (granular; none is implied by another HR permission). */
export const HR_OPS_PERMISSIONS = {
  payroll: 'hr.payroll.export',
  reports: 'hr.reports.view',
  retention: 'hr.retention.manage',
  subjectAccess: 'hr.subject_access.export',
  keys: 'hr.keys.manage',
} as const;

// ------------------------------------------------------------------------------------------------ payroll export
export const HR_PAYROLL_FORMATS = ['standard', 'brightpay'] as const;
export type HrPayrollFormat = (typeof HR_PAYROLL_FORMATS)[number];
export const HR_PAYROLL_FORMAT_LABEL: Record<HrPayrollFormat, string> = {
  standard: 'Standard (Sage, Moneysoft, Staffology and most others)',
  brightpay: 'BrightPay',
};
export const HR_PAYROLL_WHO = ['active', 'changed'] as const;
export type HrPayrollWho = (typeof HR_PAYROLL_WHO)[number];
export const HR_PAYROLL_WHO_LABEL: Record<HrPayrollWho, string> = { active: 'Everyone working here now', changed: 'Changed since a date (including leavers)' };
export interface HrPayrollInput { format: HrPayrollFormat; who: HrPayrollWho; since?: string | null }

/** One person's approved payroll details, decrypted on the server only for the export. */
export interface HrPayrollRow {
  payroll_id: string | null; first_name: string | null; middle_names: string | null; last_name: string | null; email: string | null;
  date_of_birth: string | null; ni_number: string | null; address_line1: string | null; address_line2: string | null; town: string | null;
  county: string | null; postcode: string | null; country: string | null; personal_email: string | null; personal_phone: string | null;
  start_date: string | null; leaving_date: string | null; tax_code: string | null; starter_statement: string | null; student_loan_plan: string | null;
  postgraduate_loan: boolean | null; p45_received: boolean | null; contracted_hours: number | null;
  bank_account_name: string | null; sort_code: string | null; account_number: string | null; roll_number: string | null; missing: string[];
}

const PLAN: Record<string, string> = { none: '', plan_1: 'Plan 1', plan_2: 'Plan 2', plan_4: 'Plan 4', plan_5: 'Plan 5' };
const yn = (b: boolean | null) => (b === null ? '' : b ? 'Yes' : 'No');
/** dd/mm/yyyy, what UK payroll imports expect. */
export const ukDate = (iso: string | null) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '');
const sort = (s: string | null) => (s && s.length === 6 ? `${s.slice(0, 2)}-${s.slice(2, 4)}-${s.slice(4)}` : s ?? '');

type Col = [header: string, value: (r: HrPayrollRow) => string];
const STANDARD: Col[] = [
  ['Payroll ID', r => r.payroll_id ?? ''], ['First name', r => r.first_name ?? ''], ['Middle names', r => r.middle_names ?? ''], ['Last name', r => r.last_name ?? ''],
  ['Date of birth', r => ukDate(r.date_of_birth)], ['NI number', r => r.ni_number ?? ''],
  ['Address line 1', r => r.address_line1 ?? ''], ['Address line 2', r => r.address_line2 ?? ''], ['Town', r => r.town ?? ''], ['County', r => r.county ?? ''],
  ['Postcode', r => r.postcode ?? ''], ['Country', r => r.country ?? ''], ['Email', r => r.personal_email ?? r.email ?? ''], ['Phone', r => r.personal_phone ?? ''],
  ['Start date', r => ukDate(r.start_date)], ['Leaving date', r => ukDate(r.leaving_date)], ['Tax code', r => r.tax_code ?? ''],
  ['Starter declaration', r => r.starter_statement ?? ''], ['Student loan plan', r => PLAN[r.student_loan_plan ?? ''] ?? ''], ['Postgraduate loan', r => yn(r.postgraduate_loan)],
  ['P45 received', r => yn(r.p45_received)], ['Contracted hours per week', r => (r.contracted_hours === null ? '' : String(r.contracted_hours))],
  ['Bank account name', r => r.bank_account_name ?? ''], ['Sort code', r => sort(r.sort_code)], ['Account number', r => r.account_number ?? ''],
  ['Missing details', r => r.missing.join('; ')],
];
/** BrightPay's own field names, so its "Match Header Row" maps every column (Employees > Import/Update from a CSV file). */
const BRIGHTPAY: Col[] = [
  ['Works Number', r => r.payroll_id ?? ''], ['Name', r => r.first_name ?? ''], ['Middle Name', r => r.middle_names ?? ''], ['Surname', r => r.last_name ?? ''],
  ['Date of Birth', r => ukDate(r.date_of_birth)], ['National Insurance Number', r => r.ni_number ?? ''],
  ['Address Line 1', r => r.address_line1 ?? ''], ['Address Line 2', r => r.address_line2 ?? ''], ['Address Line 3', r => r.town ?? ''], ['Address Line 4', r => r.county ?? ''],
  ['Postcode', r => r.postcode ?? ''], ['Country', r => r.country ?? ''], ['Email Address', r => r.personal_email ?? r.email ?? ''], ['Phone Number', r => r.personal_phone ?? ''],
  ['Start Date', r => ukDate(r.start_date)], ['Tax Code', r => r.tax_code ?? ''], ['Starter Declaration', r => r.starter_statement ?? ''],
  ['Student Loan Plan', r => PLAN[r.student_loan_plan ?? ''] ?? ''], ['Postgraduate Loan', r => yn(r.postgraduate_loan)],
  ['Contracted Hours Per Week', r => (r.contracted_hours === null ? '' : String(r.contracted_hours))],
  ['Payment Bank Account Name', r => r.bank_account_name ?? ''], ['Payment Bank Sort Code', r => sort(r.sort_code)], ['Payment Bank Account Number', r => r.account_number ?? ''],
];
export const HR_PAYROLL_COLUMNS: Record<HrPayrollFormat, readonly string[]> = { standard: STANDARD.map(c => c[0]), brightpay: BRIGHTPAY.map(c => c[0]) };

/** A CSV cell: quoted when needed; a leading = + - @ (or tab/CR) gets a ' so a spreadsheet never runs it as a formula. */
export function csvCell(v: string): string {
  let s = v ?? '';
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\r\n]/.test(s) || s !== s.trim() ? `"${s.replace(/"/g, '""')}"` : s;
}
export function hrPayrollCsv(rows: HrPayrollRow[], format: HrPayrollFormat): string {
  const cols = format === 'brightpay' ? BRIGHTPAY : STANDARD;
  return [cols.map(c => csvCell(c[0])).join(','), ...rows.map(r => cols.map(c => csvCell(c[1](r))).join(','))].join('\r\n') + '\r\n';
}
export const hrPayrollFileName = (format: HrPayrollFormat, today = hrToday()) => `payroll-${format}-${today}.csv`;
export function hrPayrollInputErrors(b: Partial<HrPayrollInput>, today = hrToday()): string[] {
  const out: string[] = [];
  if (!b.format || !(HR_PAYROLL_FORMATS as readonly string[]).includes(b.format)) out.push('Choose a format');
  if (!b.who || !(HR_PAYROLL_WHO as readonly string[]).includes(b.who)) out.push('Choose who to include');
  if (b.who === 'changed') {
    if (!b.since || !/^\d{4}-\d{2}-\d{2}$/.test(b.since) || !DateTime.fromISO(b.since).isValid) out.push('Choose the date to include changes since');
    else if (b.since > today) out.push('The date can\'t be in the future');
  }
  return out;
}
/** What the export dialog says under the button; also the audit wording. */
export const HR_PAYROLL_NOTE = 'Approved details only: changes waiting for approval aren\'t included. Each export is recorded in the audit log. Keep the file somewhere safe and delete it once it\'s imported.';

export interface HrExportLogRow { id: string; kind: 'payroll' | 'subject_access'; format: string | null; people: number; created_at: string; created_by_name: string | null; subject_name: string | null }

// ------------------------------------------------------------------------------------------------ starters and leavers
export interface HrStarterRow { user_id: string; display_name: string; location_name: string | null; start_date: string; complete: boolean; missing: string[]; p45_received: boolean | null; starter_statement: string | null; rtw_checked: boolean }
export interface HrLeaverRow { user_id: string; display_name: string; location_name: string | null; leaving_date: string; started: boolean; purge_on: string | null; erased: boolean }
export interface HrStartersLeavers { from: string; to: string; starters: HrStarterRow[]; leavers: HrLeaverRow[] }
/** Default range: the last 3 months to today. */
export function hrReportRange(today = hrToday()): { from: string; to: string } {
  return { from: DateTime.fromISO(today).minus({ months: 3 }).toISODate()!, to: today };
}
/** t413u: the phone's period choices for Starters and leavers. */
export const HR_REPORT_MONTHS = [1, 3, 6, 12] as const;
export function hrReportRangeMonths(months: number, today = hrToday()): { from: string; to: string } {
  return { from: DateTime.fromISO(today).minus({ months }).toISODate()!, to: today };
}
export function hrStarterLine(r: HrStarterRow): string {
  return [`Started ${hrDate(r.start_date)}`, r.complete ? 'Personal details complete' : `${r.missing.length} detail${r.missing.length === 1 ? '' : 's'} missing`,
    r.rtw_checked ? null : 'No right-to-work check'].filter(Boolean).join(' · ');
}
export function hrLeaverLine(r: HrLeaverRow): string {
  return [`Left ${hrDate(r.leaving_date)}`, r.erased ? 'Erased' : r.purge_on ? `Kept until ${hrDate(r.purge_on)}` : null, r.started ? null : 'Never started'].filter(Boolean).join(' · ');
}
export function hrStartersLeaversCsv(r: HrStartersLeavers): string {
  const head = ['Type', 'Name', 'Building', 'Date', 'Personal details', 'Right-to-work check', 'P45 received', 'Kept until'];
  const rows = [
    ...r.starters.map(s => ['Starter', s.display_name, s.location_name ?? '', ukDate(s.start_date), s.complete ? 'Complete' : `Missing: ${s.missing.join('; ')}`, s.rtw_checked ? 'Yes' : 'No', yn(s.p45_received), '']),
    ...r.leavers.map(l => ['Leaver', l.display_name, l.location_name ?? '', ukDate(l.leaving_date), l.erased ? 'Erased' : '', '', '', ukDate(l.purge_on)]),
  ];
  return [head, ...rows].map(x => x.map(csvCell).join(',')).join('\r\n') + '\r\n';
}

// ------------------------------------------------------------------------------------------------ missing details
export interface HrMissingRow { user_id: string; display_name: string; location_name: string | null; missing: string[]; reminded_at: string | null }
/** One reminder a day at most per person. */
export const HR_REMIND_GAP_HOURS = 20;
export const HR_REMIND_KIND = 'hr.details_missing';
export function hrRemindText(missing: string[]): { title: string; body: string } {
  const list = missing.slice(0, 4).join(', ') + (missing.length > 4 ? ` and ${missing.length - 4} more` : '');
  return { title: 'Please finish your HR details', body: `Still needed: ${list}. Open Settings > My account > HR details.` };
}
export const hrRemindedLine = (r: Pick<HrMissingRow, 'missing' | 'reminded_at'>) =>
  [r.missing.slice(0, 3).join(', ') + (r.missing.length > 3 ? ` +${r.missing.length - 3}` : ''), r.reminded_at ? `Reminded ${hrDate(r.reminded_at.slice(0, 10))}` : null].filter(Boolean).join(' · ');
export const canRemindNow = (remindedAt: string | null, now = Date.now()) => !remindedAt || now - Date.parse(remindedAt) >= HR_REMIND_GAP_HOURS * 3600_000;

// ------------------------------------------------------------------------------------------------ HR approvals (Home)
export interface HrApprovalsSummary { total: number; items: { id: string; user_id: string; user_name: string; label: string; requested_at: string }[] }
export const hrApprovalsTitle = 'HR changes to approve';

// ------------------------------------------------------------------------------------------------ retention
export interface HrRetention {
  /** Pay and tax records (HMRC: at least 3 years after the tax year; 6 years after leaving is the usual UK default). */
  pay_tax_years: number;
  /** Right-to-work checks and copies (Home Office: 2 years after employment ends). */
  right_to_work_years: number;
  /** Other HR documents: contracts, certificates and the rest. */
  documents_years: number;
  /** Emergency contacts: not needed once someone leaves. */
  emergency_months: number;
  /** People who left without ever starting (a withdrawn offer, a no-show). */
  unsuccessful_months: number;
  /** Documents and checks already deleted (soft) in the app: removed for good after this many days. */
  deleted_days: number;
  /** Purge automatically once a day. */
  auto_purge: boolean;
}
export const HR_RETENTION_DEFAULTS: HrRetention = { pay_tax_years: 6, right_to_work_years: 2, documents_years: 6, emergency_months: 1, unsuccessful_months: 6, deleted_days: 30, auto_purge: true };
export const HR_RETENTION_LIMITS: Record<Exclude<keyof HrRetention, 'auto_purge'>, [number, number]> = {
  pay_tax_years: [3, 10], right_to_work_years: [2, 10], documents_years: [1, 10], emergency_months: [0, 24], unsuccessful_months: [1, 24], deleted_days: [7, 365],
};
export const HR_RETENTION_FIELDS: { key: Exclude<keyof HrRetention, 'auto_purge'>; label: string; unit: 'years' | 'months' | 'days'; hint: string }[] = [
  { key: 'pay_tax_years', label: 'Pay and tax details', unit: 'years', hint: 'After leaving: name, address, NI number, tax, bank details, custom fields and approved changes. HMRC needs at least 3 years.' },
  { key: 'right_to_work_years', label: 'Right-to-work checks', unit: 'years', hint: 'After leaving: checks, share codes and passport or right-to-work copies. The Home Office asks for 2 years.' },
  { key: 'documents_years', label: 'Other HR documents', unit: 'years', hint: 'After leaving: contracts, certificates, proof of address and the rest.' },
  { key: 'emergency_months', label: 'Emergency contacts', unit: 'months', hint: 'After leaving. 0 removes them at the next purge.' },
  { key: 'unsuccessful_months', label: 'People who never started', unit: 'months', hint: 'Everything, after they were removed without starting work.' },
  { key: 'deleted_days', label: 'Deleted documents', unit: 'days', hint: 'Documents and checks deleted in the app are removed for good after this.' },
];
export function hrRetentionOf(raw: unknown): HrRetention {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Partial<HrRetention>;
  const out = { ...HR_RETENTION_DEFAULTS };
  for (const f of HR_RETENTION_FIELDS) {
    const v = r[f.key];
    if (typeof v === 'number' && Number.isInteger(v) && v >= HR_RETENTION_LIMITS[f.key][0] && v <= HR_RETENTION_LIMITS[f.key][1]) out[f.key] = v;
  }
  if (typeof r.auto_purge === 'boolean') out.auto_purge = r.auto_purge;
  return out;
}
export function hrRetentionErrors(r: Partial<HrRetention>): string[] {
  const out: string[] = [];
  for (const f of HR_RETENTION_FIELDS) {
    const v = r[f.key];
    if (v === undefined) continue;
    const [lo, hi] = HR_RETENTION_LIMITS[f.key];
    if (typeof v !== 'number' || !Number.isInteger(v) || v < lo || v > hi) out.push(`${f.label}: ${lo} to ${hi} ${f.unit}`);
  }
  return out;
}
export const hrRetentionValue = (f: (typeof HR_RETENTION_FIELDS)[number], v: number) => `${v} ${v === 1 ? f.unit.replace(/s$/, '') : f.unit}`;

export type HrPurgeCategory = 'pay_tax' | 'right_to_work' | 'documents' | 'emergency' | 'unsuccessful' | 'deleted' | 'old_bank';
export const HR_PURGE_LABEL: Record<HrPurgeCategory, string> = {
  pay_tax: 'Pay and tax details', right_to_work: 'Right-to-work checks and copies', documents: 'Other HR documents', emergency: 'Emergency contacts',
  unsuccessful: 'Everything (never started)', deleted: 'Deleted documents and checks', old_bank: 'Old bank details',
};
export interface HrPurgeItem { user_id: string | null; display_name: string | null; category: HrPurgeCategory; count: number; due_on: string }
export interface HrPurgePreview { today: string; due: HrPurgeItem[]; upcoming: HrPurgeItem[]; retention: HrRetention; last_run: { at: string; automatic: boolean; removed: number } | null }
/** How far ahead the preview's "coming up" list looks. */
export const HR_PURGE_LOOKAHEAD_DAYS = 90;
export function hrPurgeLine(i: HrPurgeItem, today = hrToday()): string {
  const what = `${HR_PURGE_LABEL[i.category]}${i.count > 1 ? ` (${i.count})` : ''}`;
  return i.due_on <= today ? what : `${what} · ${hrDate(i.due_on)}`;
}

/** The date a category's retention runs out, from the leaving date (YYYY-MM-DD). */
export function hrPurgeOn(leftOn: string, r: HrRetention, cat: Exclude<HrPurgeCategory, 'deleted' | 'old_bank'>): string {
  const d = DateTime.fromISO(leftOn, { zone: ZONE });
  const plus = cat === 'pay_tax' ? { years: r.pay_tax_years } : cat === 'right_to_work' ? { years: r.right_to_work_years } : cat === 'documents' ? { years: r.documents_years }
    : cat === 'emergency' ? { months: r.emergency_months } : { months: r.unsuccessful_months };
  return d.plus(plus).toISODate()!;
}
/** The last day anything about a leaver is kept (what the leavers report shows). */
export function hrKeptUntil(leftOn: string, started: boolean, r: HrRetention): string {
  if (!started) return hrPurgeOn(leftOn, r, 'unsuccessful');
  return [hrPurgeOn(leftOn, r, 'pay_tax'), hrPurgeOn(leftOn, r, 'right_to_work'), hrPurgeOn(leftOn, r, 'documents')].sort().at(-1)!;
}

// ------------------------------------------------------------------------------------------------ erasure
export interface HrErasurePlan {
  user_id: string; display_name: string; allowed: boolean; reason: string | null;
  erase: { category: string; label: string }[];
  keep: { category: string; label: string; until: string; why: string }[];
  erased_at: string | null;
}
export const HR_ERASE_CONFIRM = 'ERASE';

// ------------------------------------------------------------------------------------------------ subject access
export interface HrExportLink { url: string; expires_in: number; file_name: string }
export const HR_EXPORT_LINK_SECONDS = 60;
export const hrSarFileName = (name: string, today = hrToday()) => `hr-record-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'person'}-${today}.zip`;

// ------------------------------------------------------------------------------------------------ key rotation
export interface HrKeyStatus { current_version: number; available_versions: number[]; counts: { version: number; values: number }[]; unreadable: number; total: number; needs_rotation: number }
export interface HrKeyRotateResult extends HrKeyStatus { rotated: number }
export function hrKeyLine(s: HrKeyStatus): string {
  if (!s.total) return 'No encrypted HR details yet.';
  if (!s.needs_rotation) return `All ${s.total} encrypted value${s.total === 1 ? '' : 's'} use key version ${s.current_version}.`;
  return `${s.needs_rotation} of ${s.total} use an older key version.${s.unreadable ? ` ${s.unreadable} can't be opened on this server.` : ''}`;
}
